import {
    Checkbox,
    DescriptionList,
    Form,
    FormLayout,
    Label,
    Page,
    PageActions,
    Select,
    SkeletonBodyText,
    SkeletonDisplayText,
    SkeletonPage,
    LegacyStack,
    TextField,
    LegacyCard,
    Pagination,
    ResourceList,
    DataTable,
    Spinner,
} from '@shopify/polaris';
import {useCallback, useEffect, useState} from 'react';
import { useHistory } from 'react-router-dom';
import * as lodash from 'lodash';
import { FreeOtherCollectionConfiguration } from './FreeOtherCollectionConfiguration.jsx';
import { CollectionFlatRateConfiguration } from './CollectionFlatRateConfiguration.jsx';
import { BogoConfiguration } from './BogoConfiguration.jsx';
import Api from "../../helpers/api";
import formatErrors from "../../helpers/format_errors";

const emptyTypeConfig = {
    decrease: {
        type: 'decrease',
        by: {
            type: 'fixed',
            amount: '0',
        },
        eligible_products: false,
    },
    free_other_collection: {
        type: 'free_other_collection',
        origin_collection: {
            id: '',
            title: '',
            quantity: '1',
        },
        destination_collection: {
            id: '',
            title: '',
            quantity: '1',
        },
    },
    collection_flat_rate: {
        type: 'collection_flat_rate',
        min_quantity: '0',
        collection: {
            id: '',
            title: '',
        },
        price: '',
    },
    bogo: {
        type: 'bogo',
        collection: {
            id: '',
            title: '',
        },
    },
    free_shipping: {
        type: 'free_shipping',
    },
}

const emptyModel = {
    code: '',
    one_time_per_customer: false,
    stackable: true,
    automatic: false,
    configuration: emptyTypeConfig.decrease,
}

function DecreaseConfiguration ({ form, handleChange, errors }) {
    return (
        <>
            <Label>Overall adjustment</Label>
            <LegacyStack>
                <Select options={[
                            { label: 'Fixed amount', value: 'fixed' },
                            { label: 'Percentage', value: 'percentage' },
                        ]}
                        id="configuration.by.type"
                        onChange={handleChange}
                        value={form.configuration.by.type}
                        error={errors['configuration.by.type']} />

                {form.configuration.by.type === 'fixed'
                    ? <TextField prefix="$"
                                 autoComplete={'off'}
                                 value={form.configuration.by.amount}
                                 type="number"
                                 onChange={handleChange}
                                 min={0}
                                 id="configuration.by.amount"
                                 error={errors['configuration.by.amount']} />
                    : <TextField suffix="%"
                                 autoComplete={'off'}
                                 max={100}
                                 min={0}
                                 value={form.configuration.by.amount}
                                 type="number"
                                 onChange={handleChange}
                                 id="configuration.by.amount"
                                 error={errors['configuration.by.amount']} />
                }
            </LegacyStack>

            <div style={{ marginTop: '1rem' }}>
                <Checkbox checked={form.configuration.eligible_products}
                          label="Only for eligible products"
                          helpText={`Add tag "promo-${form.code}" to products that should be eligible. If you want to limit number of units, use "promo-${form.code}-NUMBER" (i.e., "promo-${form.code}-10")`}
                          id="configuration.eligible_products"
                          onChange={handleChange}
                          error={errors['configuration.eligible_products']} />
            </div>
        </>
    )
}

function DetailedConfigurationSwitch ({ form, handleChange, errors }) {
    const props = { form, handleChange, errors};

    switch (form.configuration.type) {
        case 'decrease':
            return (<DecreaseConfiguration {...props} />);

        case 'free_other_collection':
            return (<FreeOtherCollectionConfiguration {...props} />);

        case 'collection_flat_rate':
            return (<CollectionFlatRateConfiguration {...props} />);

        case 'bogo':
            return (<BogoConfiguration {...props} />);

        default:
            return null;
    }
}

export const Skeleton = () => (
    <SkeletonPage primaryAction>
        <FormLayout>
            <FormLayout.Group>
                <LegacyStack vertical>
                    <SkeletonBodyText />
                    <SkeletonDisplayText />
                </LegacyStack>
            </FormLayout.Group>
        </FormLayout>
    </SkeletonPage>
)

export default ({ model = emptyModel, handleSubmit, saving = false, errors = {}, handleDelete = () => {}, stats = null }) => {
    const history = useHistory();
    const [form, setForm] = useState(model);
    const [changed, setChanged] = useState(false);
    const [loadingLineItems, setLoadingLineItems] = useState(false);
    const [lineItems, setLineItems] = useState([]);
    const [lineItemsMeta, setLineItemsMeta] = useState({
        currentPage: 1,
        lastPage: 1,
    });

    const handleChange = useCallback((value, id) => {
        setChanged(true);

        const updatedForm = lodash.cloneDeep(form);
        lodash.set(updatedForm, id, value);

        if (form.configuration.type !== updatedForm.configuration.type) {
            updatedForm.configuration = emptyTypeConfig[updatedForm.configuration.type];
        }

        setForm(updatedForm);
    }, [form]);

    const submitForm = useCallback(() => handleSubmit(form), [form, handleSubmit]);

    useEffect(() => {
        if (model.id) {
            loadLineItems();
        }
    }, []);

    const loadLineItems = useCallback((page = 1) => {
        if (model.id) {
            setLoadingLineItems(true);
            Api
                .getDiscountedLineItems(model.id, page)
                .then((response) => {
                    setLineItems(response.data.data);
                    setLineItemsMeta({
                        currentPage: response.data.current_page,
                        lastPage: response.data.last_page,
                    });
                })
                .finally(() => setLoadingLineItems(false));
        }
    })

    const LineItemsTable = () => {
        return (<DataTable
            columnContentTypes={[
                'text',
                'numeric',
                'numeric',
                'numeric',
                'numeric',
            ]}
            headings={[
                'Product',
                'List Price Per Unit',
                'Total Discount',
                'No. of Uses',
                'Total Sales After Discount',
            ]}
            rows={lineItems.map((item) => {
                return [
                    item.name,
                    `$${item.unit_price.toFixed(2)}`,
                    `$${item.total_discount.toFixed(2)}`,
                    `${item.quantity}`,
                    `$${item.total_price_after_discount.toFixed(2)}`,
                ];
            })}
        />);
    };

    return (
        <Page title={model.id ? 'Edit Discount Code' : 'Create Discount Code'}
              backAction={{
                      content: 'Back',
                      onAction: history.goBack,
                  }}
              primaryAction={{
                  content: saving ? 'Saving' : 'Save',
                  disabled: !changed || saving,
                  onAction: submitForm
              }}>
            <Form>
                <LegacyCard sectioned>
                    <TextField autoComplete={'off'} value={form.code} onChange={handleChange} label="Code" type="text" id="code" error={errors.code}/>

                    <LegacyStack spacing={"loose"}>
                        <Checkbox checked={form.one_time_per_customer}
                                  label="One time per customer"
                                  id="one_time_per_customer"
                                  onChange={handleChange}
                                  error={errors.one_time_per_customer} />

                        <Checkbox checked={form.stackable}
                                  label="Is Stackable"
                                  id="stackable"
                                  onChange={handleChange}
                                  error={errors.stackable} />
                    </LegacyStack>

                    <Select options={[
                        { label: 'Decrease', value: 'decrease' },
                        { label: 'Free in other collection', value: 'free_other_collection' },
                        { label: 'Collection flat rate', value: 'collection_flat_rate' },
                        { label: 'Buy one, get another for free', value: 'bogo' },
                        { label: 'Free Shipping', value: 'free_shipping' },
                    ]}
                            value={form.configuration.type}
                            id="configuration.type"
                            label="Discount type"
                            onChange={handleChange}
                            error={errors['configuration.type']} />

                    <DetailedConfigurationSwitch form={form} errors={errors} handleChange={handleChange} />

                    <PageActions primaryAction={{
                        content: saving ? 'Saving' : 'Save',
                        onAction: submitForm,
                        disabled: !changed || saving
                    }}
                                 secondaryActions={[{
                                     content: saving ? 'Deleting' : 'Delete',
                                     disabled: saving,
                                     onAction: handleDelete,
                                     destructive: true
                                 }]} />
                </LegacyCard>

                {model.id && (<>

                    <LegacyCard sectioned title={"Stats"}>
                        <DescriptionList items={[
                            {term: "Number of Uses", description: stats.number_of_usage ?? 0},
                            {term: "Total Sales After Discount", description: "$" + (stats.total_sales ?? 0.0).toFixed(2)},
                            {term: "Total Discounted amount", description: "$" + (stats.total_discount_amount ?? 0.0).toFixed(2)},
                            {term: "Average Discounted amount", description: "$" + (stats.average_discount_amount ?? 0.0).toFixed(2)},
                        ]} />
                    </LegacyCard>

                    <LegacyCard sectioned title={"Discounted Line Items"}>
                        <Pagination
                            label={`Showing Page ${lineItemsMeta.currentPage} of ${lineItemsMeta.lastPage}`}
                            hasPrevious={ lineItemsMeta.currentPage > 1}
                            onPrevious={() => { loadLineItems(lineItemsMeta.currentPage - 1) }}
                            hasNext={ lineItemsMeta.currentPage < lineItemsMeta.lastPage }
                            onNext={() => { loadLineItems(lineItemsMeta.currentPage + 1) }} />

                        <br />

                        {loadingLineItems && (<Spinner />) }
                        {!loadingLineItems && (<LineItemsTable />) }

                        <br />
                        <Pagination
                            label={`Showing Page ${lineItemsMeta.currentPage} of ${lineItemsMeta.lastPage}`}
                            hasPrevious={ lineItemsMeta.currentPage > 1}
                            onPrevious={() => { loadLineItems(lineItemsMeta.currentPage - 1) }}
                            hasNext={ lineItemsMeta.currentPage < lineItemsMeta.lastPage }
                            onNext={() => { loadLineItems(lineItemsMeta.currentPage + 1) }} />
                    </LegacyCard>
                </>)}
            </Form>
        </Page>
    )
}
